// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

import React, { useState, useEffect, useRef } from "react";
import { sGetRaw } from "../lib/scopedStorage";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface Message {
  role: "user" | "assistant";
  content: string;
  error?: boolean;
}

interface Props {
  contextDoc: string;
  projectName: string;
  onClose: () => void;
}

const SUGGESTIONS = [
  "What's driving the highest cost in this estimate?",
  "Which line items are still marked as Allowance?",
  "Who are we carrying for MEP trades?",
  "What's our cost per SF and how does overhead affect it?",
  "Are there any Needs Review items I should address?",
  "Summarize the bid coverage — what scopes are unleveled?",
];

export default function ProjectChatPanel({ contextDoc, projectName, onClose }: Props) {
  const [isDarkMode] = useState<boolean>(() => {
    try { const s = sGetRaw("oe_dark_mode"); return s === null ? true : s === "true"; } catch { return true; }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const [messages, setMessages]     = useState<Message[]>([]);
  const [input, setInput]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [convHistory, setConvHistory] = useState<Array<{ role: string; content: string }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSend = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: q }]);
    setLoading(true);

    try {
      const { supabase } = await import("../lib/supabase");
      if (!supabase) throw new Error("Not connected — please sign in.");

      const { data, error } = await supabase.functions.invoke("project-assistant", {
        body: {
          question: q,
          contextDocument: contextDoc,
          conversationHistory: convHistory,
        },
      });

      if (error) {
        let detail = error.message;
        try { const b = await (error as any).context?.json?.(); detail = b?.error || b?.message || detail; } catch {}
        throw new Error(detail);
      }
      if (data?.error) throw new Error(data.error);

      const answer: string = data?.answer || "No response returned.";
      setMessages(prev => [...prev, { role: "assistant", content: answer }]);
      setConvHistory(prev => [
        ...prev,
        { role: "user", content: q },
        { role: "assistant", content: answer },
      ].slice(-20));
    } catch (e: any) {
      setMessages(prev => [...prev, { role: "assistant", content: `Error: ${e.message || "Something went wrong."}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 300,
      width: "min(500px, 100vw)",
      display: "flex", flexDirection: "column",
      background: T.bgPanel,
      borderLeft: `1px solid ${T.borderPrimary}`,
      boxShadow: "-8px 0 32px rgba(0,0,0,.28)",
      fontFamily: FONT,
    }}>

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "14px 18px", borderBottom: `1px solid ${T.borderPrimary}`,
        flexShrink: 0,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: "50%",
          background: `${T.borderAccent}22`, border: `1px solid ${T.borderAccentFaint}`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon.Search size={14} color={T.borderAccent}/>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary }}>Project Assistant</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>
            {projectName} · context loaded
          </div>
        </div>
        <button onClick={onClose}
          style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: "4px 6px", display: "flex", lineHeight: 1 }}>
          <Icon.X size={16}/>
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 }}>

        {messages.length === 0 && !loading && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 20 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ marginBottom: 10, opacity: 0.4 }}><Icon.MessageSquare size={24} /></div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 6 }}>Ask anything about this project</div>
              <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
                I can look up costs, quantities, trade assignments,<br/>
                confidence gaps, and anything in the estimate.
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => { setInput(s); inputRef.current?.focus(); }}
                  style={{
                    background: `${T.borderAccent}0c`, border: `1px solid ${T.borderAccentFaint}`,
                    borderRadius: 6, padding: "8px 12px", textAlign: "left", cursor: "pointer",
                    fontSize: 11, color: T.textTertiary, fontFamily: FONT, lineHeight: 1.4,
                    transition: "background .12s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = `${T.borderAccent}18`)}
                  onMouseLeave={e => (e.currentTarget.style.background = `${T.borderAccent}0c`)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "85%", padding: "10px 14px",
              borderRadius: msg.role === "user" ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
              background: msg.role === "user"
                ? T.borderAccent
                : msg.error ? `${T.negative}18` : T.bgApp,
              border: msg.role === "user" ? "none"
                : msg.error ? `1px solid ${T.negative}44` : `1px solid ${T.borderPrimary}`,
              color: msg.role === "user" ? T.textOnGold : msg.error ? T.negative : T.textSecondary,
              fontSize: 12, lineHeight: 1.65,
              whiteSpace: "pre-wrap" as any, wordBreak: "break-word" as any,
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{
              padding: "10px 14px", borderRadius: "10px 10px 10px 3px",
              background: T.bgApp, border: `1px solid ${T.borderPrimary}`,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ display: "inline-flex", gap: 4 }}>
                {[0, 1, 2].map(n => (
                  <span key={n} style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: T.borderAccent, opacity: 0.6,
                    animation: `oe-typing-dot 1.2s ${n * 0.2}s ease-in-out infinite`,
                  }}/>
                ))}
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef}/>
      </div>

      {/* Input */}
      <div style={{
        padding: "12px 18px 16px",
        borderTop: `1px solid ${T.borderPrimary}`,
        flexShrink: 0, background: T.bgPanel,
      }}>
        {convHistory.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
            <button onClick={() => { setMessages([]); setConvHistory([]); }}
              style={{ background: "none", border: "none", fontSize: 10, color: T.textMuted, cursor: "pointer", fontFamily: FONT }}>
              Clear conversation
            </button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about this project…"
            rows={2}
            style={{
              flex: 1, resize: "none" as any, borderRadius: 10,
              border: `1px solid ${input.trim() ? T.borderAccent + "88" : T.borderSecondary}`,
              background: T.bgApp, color: T.textSecondary,
              fontSize: 12, fontFamily: FONT, lineHeight: 1.5,
              padding: "8px 12px", outline: "none",
              transition: "border-color .15s",
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            style={{
              flexShrink: 0, width: 36, height: 36, borderRadius: 10, border: "none",
              background: input.trim() && !loading ? T.borderAccent : T.borderSecondary,
              color: input.trim() && !loading ? T.textOnGold : T.textMuted,
              cursor: input.trim() && !loading ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background .15s",
            }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M1 1l14 7-14 7V9.5l10-1.5L1 6.5V1z"/>
            </svg>
          </button>
        </div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 6, textAlign: "center" }}>
          Enter to send · Shift+Enter for new line
        </div>
      </div>

      <style>{`
        @keyframes oe-typing-dot {
          0%, 100% { transform: translateY(0); opacity: .5; }
          50% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
